import { useTranslation } from "react-i18next"

import type { MessagesFigures } from "@/api/generated/model"
import { SummaryCards, type SummaryItem } from "@/components/kit/SummaryCards"
import { formatCount, formatPercent, localeOf } from "@/lib/format"
import { delta } from "../lib/overview"

/**
 * FR-DSH-OVW-03: sent, delivery rate, failed and received for the range,
 * each against the previous window of the same length, or "no comparison".
 */
export function Figures({ figures }: { figures: MessagesFigures }) {
  const { t, i18n } = useTranslation("overview")
  const locale = localeOf(i18n.language)
  const count = (k: "sent" | "failed" | "received"): SummaryItem => ({
    id: k,
    label: t(`figures.${k}`),
    value: formatCount(figures[k].current, locale),
    delta: delta(figures[k].current, figures[k].previous),
    ...(k === "failed" && { better: "down" as const }),
  })
  const rate = figures.deliveryRate
  const items: SummaryItem[] = [
    count("sent"),
    {
      id: "deliveryRate",
      label: t("figures.deliveryRate"),
      value: rate.current === null ? "—" : formatPercent(rate.current, locale),
      delta: rate.current === null ? "no-comparison" : delta(rate.current, rate.previous, "points"),
    },
    count("failed"),
    count("received"),
  ]
  const empty = figures.sent.current === 0 && figures.received.current === 0

  return (
    <div className="space-y-2">
      <SummaryCards items={items} locale={locale} against={t("figures.against")} />
      {empty ? <p className="text-sm text-muted-foreground">{t("figures.empty")}</p> : null}
    </div>
  )
}
